import { createFileRoute, useParams, useNavigate, useSearch, Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs'
import { useJobs, useCreateJob, useCancelJob, jobTypeConfig, getJobTypeName, getJobTypeIcon, type JobsListParams } from '../../../api/jobs'
import { useDocuments } from '../../../hooks/useQueries'
import { useApi } from '../../../api/hooks'
import { useLiveSubscription, useScopeLive } from '../../../contexts/LiveEventsContext'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select'
import { SearchableSelect } from '../../../components/SearchableSelect'
import { JobsAIUsageCard } from '../../../components/jobs/JobsAIUsageCard'
import { ProcessingBlockersBanner } from '../../../components/jobs/ProcessingBlockersBanner'
import { AIProviderBanner } from '../../../components/jobs/AIProviderBanner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../../components/ui/table'
import { Badge } from '../../../components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../components/ui/dialog'
import { Label } from '../../../components/ui/label'
import { Checkbox } from '../../../components/ui/checkbox'
import { Textarea } from '../../../components/ui/textarea'
import { AppIcon } from '../../../components/ui/icon'
import {
  mdiPlus,
  mdiClockOutline,
  mdiLoading,
  mdiCheckCircle,
  mdiAlertCircle,
  mdiCancel,
  mdiAlert,
  mdiChevronLeft,
  mdiChevronRight,
  mdiStop,
  mdiRefresh,
  mdiCog,
  mdiCheck,
  mdiSitemap,
} from '@mdi/js'
import { PageHeader } from '../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../lib/section-icons'
import { EmptyState } from '../../../components/ui/empty-state'

export const Route = createFileRoute('/$org/jobs/')({
  component: JobsList,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      type: (search.type as string) || undefined,
      status: (search.status as string) || undefined,
      limit: typeof search.limit === 'number' ? search.limit : 30,
      offset: typeof search.offset === 'number' ? search.offset : 0,
      // On by default: without it the page is a wall of empty org-wide sweeps
      // and the runs a human came for are buried (spec 2026-09-05-08 §E). Only
      // an explicit `false` in the URL turns it off, so the choice is
      // bookmarkable and shareable like every other filter here.
      hide_noop: search.hide_noop === false || search.hide_noop === 'false' ? false : true,
    }
  },
})


const statusConfig = {
  pending: { color: 'bg-muted text-muted-foreground', icon: mdiClockOutline, animated: false },
  running: { color: 'bg-primary/10 text-primary', icon: mdiLoading, animated: true },
  success: { color: 'bg-muted text-foreground', icon: mdiCheckCircle, animated: false },
  failed: { color: 'bg-destructive/10 text-destructive', icon: mdiAlertCircle, animated: false },
  failed_retry: { color: 'bg-muted text-muted-foreground', icon: mdiRefresh, animated: false },
  cancelled: { color: 'bg-muted text-muted-foreground', icon: mdiCancel, animated: false },
  lost: { color: 'bg-muted text-muted-foreground', icon: mdiAlert, animated: false },
  manual: { color: 'bg-muted text-muted-foreground', icon: mdiAlert, animated: false },
}

/**
 * isRejectedKeyFailure spots a run that died on the credential rather than on
 * its own work. Matched on the provider's own machine-readable code, never on
 * the prose around it — Google rewords the message, not the reason.
 */
function isRejectedKeyFailure(job: { final_status?: string | null; last_execution?: { error?: unknown } | null }) {
  if (job.final_status !== 'failed') return false
  const error = job.last_execution?.error
  if (!error) return false
  // The stored blob is `{"error": "<provider message>"}`; stringifying it is
  // the shape-proof way to look for the code inside it.
  return JSON.stringify(error).includes('API_KEY_INVALID')
}

function JobsList() {
  const { org } = useParams({ from: '/$org/jobs' })
  const navigate = useNavigate({ from: Route.fullPath })
  const search = useSearch({ from: Route.fullPath }) as JobsListParams
  // A list filtered on a final status is read for when the work ended, not
  // when it was planned (#1508): the date column then shows the finish time,
  // which is when final_status was written (updated_at).
  const showsFinishedAt = search.status === 'success' || search.status === 'failed' || search.status === 'cancelled'
  const { request } = useApi()
  const { t } = useTranslation('jobs')

  // Live entity notifications for the jobs collection scope — creating,
  // updating, or cancelling ANY job in the org, plus any job_executions
  // status transition, dirties this scope (migration 094_job_events_notify).
  // useJobs' own refetchInterval (passed `live` below) downshifts to a 30s
  // safety net while acked, and stops entirely once no job is
  // pending/running; freshness otherwise comes from the WS `update` frame's
  // debounced cache invalidation in LiveEventsContext.tsx.
  useLiveSubscription({ entity: 'jobs' })
  const isJobsScopeLive = useScopeLive({ entity: 'jobs' })

  const [createJobOpen, setCreateJobOpen] = useState(false)
  const [testJobsOpen, setTestJobsOpen] = useState(false)
  const [newJobType, setNewJobType] = useState('')
  const [newJobProperties, setNewJobProperties] = useState('')
  const [jobStatus, setJobStatus] = useState<'idle' | 'creating' | 'success' | 'error'>('idle')
  const [jobError, setJobError] = useState<string | null>(null)

  useBreadcrumbs([
    { title: t('common:nav.home'), path: `/${org}` },
    { title: t('common:nav.settings'), path: `/${org}/settings` },
    { title: t('list.title') }
  ])

  const { data, isLoading, isFetching, error, refetch } = useJobs(org, search, isJobsScopeLive)
  // How many rows the "Hide no-op runs" filter removed — shown next to the
  // toggle so it never silently swallows rows.
  const noopHiddenCount = data?.noop_hidden_count ?? 0
  const createJobMutation = useCreateJob(org)
  const cancelJobMutation = useCancelJob(org)

  // Fetch documents for job processing demonstration
  const { data: documents = [] } = useDocuments(org)

  const handleFilterChange = (key: keyof JobsListParams, value: string | number | undefined) => {
    const newSearch = { ...search, [key]: value }
    if (key !== 'offset') {
      newSearch.offset = 0 // Reset to first page when filtering
    }
    navigate({ search: newSearch as any })
  }

  const jobTypeOptions = useMemo(() => {
    return Object.entries(jobTypeConfig)
      .map(([value, { icon, label }]) => ({
        value,
        label: t(`types.${value}`, { defaultValue: label }),
        icon,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [t])

  const handleCreateJob = async () => {
    try {
      let properties = undefined
      if (newJobProperties.trim()) {
        properties = JSON.parse(newJobProperties)
      }

      await createJobMutation.mutateAsync({
        type: newJobType,
        properties,
      })

      setCreateJobOpen(false)
      setNewJobType('')
      setNewJobProperties('')
    } catch (error) {
      console.error('Failed to create job:', error)
    }
  }

  // Function to create an asset hierarchy job
  const handleCreateAssetHierarchyJob = async (refresh: boolean = false) => {
    setJobStatus('creating')
    setJobError(null)

    try {
      const response = await request('/api/v1/organizations/{orgCode}/jobs', 'POST', {
        params: {
          path: { orgCode: org }
        },
        body: {
          type: 'asset_hierarchy',
          properties: refresh ? { refresh: true } : {}
        }
      })

      if (response) {
        setJobStatus('success')
        refetch() // Refresh the jobs list
        // Reset status after 3 seconds
        setTimeout(() => {
          setJobStatus('idle')
        }, 3000)
      }
    } catch (error) {
      setJobError((error as any)?.title || (error as any)?.message || 'Failed to create asset hierarchy job')
      setJobStatus('error')
      // Reset status after 5 seconds
      setTimeout(() => {
        setJobStatus('idle')
        setJobError(null)
      }, 5000)
    }
  }

  // Function to create an asset type hierarchy job
  const handleCreateAssetTypeHierarchyJob = async () => {
    setJobStatus('creating')
    setJobError(null)
    try {
      const response = await request('/api/v1/organizations/{orgCode}/jobs', 'POST', {
        params: {
          path: { orgCode: org }
        },
        body: {
          type: 'asset_type_hierarchy',
          properties: {}
        }
      })
      if (response) {
        setJobStatus('success')
        refetch() // Refresh the jobs list
        // Reset status after 3 seconds
        setTimeout(() => {
          setJobStatus('idle')
        }, 3000)
      }
    } catch (error) {
      setJobError((error as any)?.title || (error as any)?.message || 'Failed to create asset type hierarchy job')
      setJobStatus('error')
      // Reset status after 5 seconds
      setTimeout(() => {
        setJobStatus('idle')
        setJobError(null)
      }, 5000)
    }
  }

  // Function to create an email job
  const handleCreateEmailJob = async () => {
    setJobStatus('creating')
    setJobError(null)

    try {
      const response = await request('/api/v1/organizations/{orgCode}/jobs', 'POST', {
        params: {
          path: { orgCode: org }
        },
        body: {
          type: 'email',
          properties: {
            type: 'welcome',
            to: ['florent.clairambault@gmail.com'],
            properties: {
              UserName: 'Florent',
              OrganizationName: 'demo',
              InviterName: 'Florent',
              SetupPasswordURL: 'https://zoro.io/setup-password'
            }
          }
        }
      })

      if (response) {
        setJobStatus('success')
        refetch() // Refresh the jobs list
        // Reset status after 3 seconds
        setTimeout(() => {
          setJobStatus('idle')
        }, 3000)
      }
    } catch (error) {
      setJobError((error as any)?.title || (error as any)?.message || 'Failed to create email job')
      setJobStatus('error')
      // Reset status after 5 seconds
      setTimeout(() => {
        setJobStatus('idle')
        setJobError(null)
      }, 5000)
    }
  }

  // Function to create a document processing job
  const handleCreateDocProcessingJob = async (type: 'doc_text_extraction' | 'doc_meta_extraction' | 'doc_embedding' | 'doc_classification') => {
    // Get the first document for demonstration
    const document = documents[0]
    if (!document) {
      setJobError('No documents available for processing')
      setJobStatus('error')
      return
    }

    setJobStatus('creating')
    setJobError(null)

    try {
      const response = await request('/api/v1/organizations/{orgCode}/jobs', 'POST', {
        params: {
          path: { orgCode: org }
        },
        body: {
          type: type,
          properties: {
            document_uid: document.uid
          }
        }
      })

      if (response) {
        setJobStatus('success')
        refetch() // Refresh the jobs list
        // Reset status after 3 seconds
        setTimeout(() => {
          setJobStatus('idle')
        }, 3000)
      }
    } catch (error) {
      setJobError((error as any)?.title || (error as any)?.message || 'Failed to create job')
      setJobStatus('error')
      // Reset status after 5 seconds
      setTimeout(() => {
        setJobStatus('idle')
        setJobError(null)
      }, 5000)
    }
  }

  const handleCancelJob = async (jobUid: string) => {
    try {
      await cancelJobMutation.mutateAsync(jobUid)
    } catch (error) {
      console.error('Failed to cancel job:', error)
    }
  }

  const getJobStatus = (job: any) => {
    if (job.final_status) {
      return job.final_status
    }

    // If no final status but has last_execution_uid, show as running
    if (job.last_execution) {
      return job.last_execution.status || 'pending'
    }

    return 'pending'
  }

  const canCancelJob = (job: any) => {
    return !job.final_status || (job.final_status !== 'success' && job.final_status !== 'failed' && job.final_status !== 'cancelled')
  }

  const formatDuration = (timeSpentMs: number): string => {
    if (!timeSpentMs || timeSpentMs <= 0) return '--'

    const totalSeconds = Math.floor(timeSpentMs / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    if (hours > 0) {
      return `${hours}h ${minutes}m`
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`
    } else if (seconds > 0) {
      return `${seconds}s`
    } else {
      return `${timeSpentMs}ms`
    }
  }


  const handleManualRefresh = () => {
    refetch()
  }

  if (error) {
    return (
      <div>
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <h3 className="text-lg font-medium text-destructive">{t('list.error_loading')}</h3>
          <p className="text-destructive mt-1">{(error as any)?.title || (error as any)?.message || t('common:status.error')}</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <AIProviderBanner org={org} />
      <ProcessingBlockersBanner org={org} />
      <PageHeader
        className="mb-6"
        icon={SECTION_ICONS.jobs}
        title={t('list.title')}
        actions={
        <>
          <Button
            variant="outline"
            onClick={handleManualRefresh}
          >
            <AppIcon path={mdiRefresh} size="md" className="mr-2" spin={isFetching} />
            {t('common:actions.refresh')}
          </Button>

          <Dialog open={createJobOpen} onOpenChange={setCreateJobOpen}>
            <DialogTrigger asChild>
              <Button>
                <AppIcon path={mdiPlus} size="md" className="mr-2" />
                {t('create.button')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('create.title')}</DialogTitle>
                <DialogDescription>
                  {t('create.description')}
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="type" className="text-right">
                    {t('create.type_label')}
                  </Label>
                  <Input
                    id="type"
                    value={newJobType}
                    onChange={(e) => setNewJobType(e.target.value)}
                    className="col-span-3"
                    placeholder={t('create.type_placeholder')}
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="properties" className="text-right">
                    {t('create.properties_label')}
                  </Label>
                  <Textarea
                    id="properties"
                    value={newJobProperties}
                    onChange={(e) => setNewJobProperties(e.target.value)}
                    className="col-span-3"
                    placeholder={t('create.properties_placeholder')}
                    rows={4}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={handleCreateJob}
                  disabled={!newJobType || createJobMutation.isPending}
                >
                  {createJobMutation.isPending ? t('create.creating') : t('create.submit')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
        }
      />

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1">
          <Label htmlFor="type-filter">{t('list.filter_type')}</Label>
          <SearchableSelect
            options={jobTypeOptions}
            value={search.type ?? ''}
            onValueChange={(v) => handleFilterChange('type', v || undefined)}
            placeholder={t('list.all_types')}
            searchPlaceholder={t('list.type_search_placeholder')}
            emptyMessage={t('list.type_empty_message')}
            data-testid="jobs-type-filter"
          />
        </div>

        <div className="flex-1">
          <Label htmlFor="status-filter">{t('list.filter_status')}</Label>
          <Select value={search.status || 'all'} onValueChange={(value) => handleFilterChange('status', value === 'all' ? undefined : value)}>
            <SelectTrigger id="status-filter" data-testid="jobs-status-filter">
              <SelectValue placeholder={t('list.all_statuses')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('list.all_statuses')}</SelectItem>
              <SelectItem value="executing" data-testid="jobs-status-filter-executing">{t('list.status_executing')}</SelectItem>
              <SelectItem value="queued" data-testid="jobs-status-filter-queued">{t('list.status_queued')}</SelectItem>
              {/* The API reads status=running as "no final status yet": queued or running. */}
              <SelectItem value="running" data-testid="jobs-status-filter-in-progress">{t('list.status_in_progress')}</SelectItem>
              <SelectItem value="success">{t('list.status_success')}</SelectItem>
              <SelectItem value="failed">{t('list.status_failed')}</SelectItem>
              <SelectItem value="cancelled">{t('list.status_cancelled')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 flex flex-col justify-end pb-1">
          <div className="flex items-center gap-2">
            <Checkbox
              id="hide-noop-filter"
              checked={search.hide_noop !== false}
              onCheckedChange={(checked) => handleFilterChange('hide_noop', checked === true ? undefined : 'false')}
              data-testid="jobs-hide-noop-toggle"
            />
            <Label htmlFor="hide-noop-filter" className="cursor-pointer" title={t('list.hide_noop_hint')}>
              {t('list.hide_noop')}
            </Label>
          </div>
          {noopHiddenCount > 0 && (
            <span className="text-xs text-muted-foreground mt-1" data-testid="jobs-noop-hidden-count">
              {t('list.noop_hidden', { count: noopHiddenCount })}
            </span>
          )}
        </div>
      </div>

      {/*
        What the organization spends on paid AI (spec 2026-08-16-08 §4). Above
        the table because it describes the whole list, and it hides itself when
        there is no AI spend at all.
      */}
      <JobsAIUsageCard org={org} />

      {/* Jobs Table */}
      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <AppIcon path={mdiLoading} spin size="lg" />
          <span className="ml-2">{t('list.loading')}</span>
        </div>
      ) : (
        <>
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('list.column_type')}</TableHead>
                  <TableHead>{t('list.column_status')}</TableHead>
                  <TableHead>{t('list.column_priority')}</TableHead>
                  <TableHead data-testid="jobs-date-column">
                    {showsFinishedAt ? t('list.column_finished') : t('list.column_scheduled')}
                  </TableHead>
                  <TableHead>{t('list.column_duration')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.jobs.map((job) => {
                  const status = getJobStatus(job)
                  const statusInfo = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending

                  return (
                    <TableRow key={job.uid}>
                      <TableCell>
                        <Link
                          to="/$org/jobs/$jobUid"
                          params={{ org, jobUid: job.uid }}
                          className="font-medium text-primary hover:text-primary/80 flex items-center gap-1.5"
                        >
                          {getJobTypeIcon(job.type) && (
                            <AppIcon path={getJobTypeIcon(job.type)!} size="sm" />
                          )}
                          {t(`types.${job.type}`, { defaultValue: getJobTypeName(job.type) })}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusInfo.color}>
                          <AppIcon
                            path={statusInfo.icon}
                            size="xs"
                            className={`mr-1 ${statusInfo.animated ? 'motion-safe:animate-spin' : ''}`}
                          />
                          {t(`list.status_${status}`, { defaultValue: status })}
                        </Badge>
                        {/*
                          A red row from three days ago must still hand the
                          reader the fix, not a stack of provider JSON (spec
                          2026-09-10-03 §3). The rejected-key rows outlive the
                          banner: the banner goes away the moment the key is
                          replaced, these stay in the history forever.
                        */}
                        {isRejectedKeyFailure(job) && (
                          <Link
                            to="/admin/settings"
                            className="ml-2 text-xs underline text-muted-foreground"
                            data-testid="job-row-cta-configure_gemini_key"
                          >
                            {t('ai_provider.row_cta')}
                          </Link>
                        )}
                      </TableCell>
                      <TableCell>{job.priority}</TableCell>
                      <TableCell data-testid={`job-row-date-${job.uid}`}>
                        {new Date(showsFinishedAt ? job.updated_at : job.scheduled_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {job.final_status ? (
                          <span className="text-sm font-mono text-muted-foreground">
                            {job.last_execution?.time_spent_ms
                              ? formatDuration(job.last_execution.time_spent_ms)
                              : '--'
                            }
                          </span>
                        ) : (
                          canCancelJob(job) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCancelJob(job.uid)}
                              disabled={cancelJobMutation.isPending}
                            >
                              <AppIcon path={mdiStop} size="sm" className="mr-1" />
                              {t('cancel.button')}
                            </Button>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
                {(!data?.jobs || data.jobs.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <EmptyState variant="inline" live title={t('list.empty')} />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {data?.pagination && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                {t('list.showing', {
                  from: (search.offset || 0) + 1,
                  to: Math.min((search.offset || 0) + (search.limit || 30), data.pagination.total),
                  total: data.pagination.total
                })}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleFilterChange('offset', Math.max(0, (search.offset || 0) - (search.limit || 30)))}
                  disabled={(search.offset || 0) === 0}
                >
                  <AppIcon path={mdiChevronLeft} size="sm" className="mr-1" />
                  {t('common:actions.back')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleFilterChange('offset', (search.offset || 0) + (search.limit || 30))}
                  disabled={!data.pagination.has_more}
                >
                  Next
                  <AppIcon path={mdiChevronRight} size="sm" className="ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Testing Jobs Dialog */}
      <div className="flex justify-center mt-6">
        <Dialog open={testJobsOpen} onOpenChange={setTestJobsOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="testing-jobs-button">
              🐛 {t('testing.button')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('testing.title')}</DialogTitle>
              <DialogDescription>
                {t('testing.description')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Status Message */}
              {jobStatus === 'success' && (
                <div className="flex items-center p-3 bg-muted rounded-lg text-foreground">
                  <AppIcon path={mdiCheck} size="lg" className="mr-2" />
                  <span className="text-sm font-medium">{t('testing.job_created')}</span>
                </div>
              )}
              {jobStatus === 'error' && (
                <div className="flex items-center p-3 bg-destructive/10 rounded-lg text-destructive">
                  <AppIcon path={mdiAlert} size="lg" className="mr-2" />
                  <span className="text-sm font-medium">{jobError || t('testing.job_failed')}</span>
                </div>
              )}

              {/* Document Processing Jobs */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-foreground">{t('testing.doc_processing')}</h3>
                <p className="text-xs text-muted-foreground mb-2">
                  {documents.length > 0
                    ? t('testing.doc_processing_description_available', { name: documents[0].name, filename: documents[0].file_name })
                    : t('testing.doc_processing_description_empty')
                  }
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    onClick={() => handleCreateDocProcessingJob('doc_text_extraction')}
                    disabled={jobStatus === 'creating' || documents.length === 0}
                    variant="outline"
                    className="w-full"
                  >
                    <AppIcon path={mdiCog} size="md" className="mr-2" />
                    {t('testing.ocr')}
                  </Button>
                  <Button
                    onClick={() => handleCreateDocProcessingJob('doc_embedding')}
                    disabled={jobStatus === 'creating' || documents.length === 0}
                    variant="outline"
                    className="w-full"
                  >
                    <AppIcon path={mdiCog} size="md" className="mr-2" />
                    {t('testing.embedding')}
                  </Button>
                  <Button
                    onClick={() => handleCreateDocProcessingJob('doc_classification')}
                    disabled={jobStatus === 'creating' || documents.length === 0}
                    variant="outline"
                    className="w-full"
                  >
                    <AppIcon path={mdiCog} size="md" className="mr-2" />
                    {t('testing.classification')}
                  </Button>
                  <Button
                    onClick={() => handleCreateDocProcessingJob('doc_meta_extraction')}
                    disabled={jobStatus === 'creating' || documents.length === 0}
                    variant="outline"
                    className="w-full"
                  >
                    <AppIcon path={mdiCog} size="md" className="mr-2" />
                    {t('testing.data_extraction')}
                  </Button>
                </div>
              </div>

              {/* Asset Hierarchy Jobs */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-foreground">{t('testing.asset_hierarchy')}</h3>
                <p className="text-xs text-muted-foreground mb-2">
                  {t('testing.asset_hierarchy_description')}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    onClick={() => handleCreateAssetHierarchyJob(false)}
                    disabled={jobStatus === 'creating'}
                    variant="outline"
                    className="w-full"
                  >
                    <AppIcon path={mdiSitemap} size="md" className="mr-2" />
                    {t('testing.process_hierarchy')}
                  </Button>
                  <Button
                    onClick={() => handleCreateAssetHierarchyJob(true)}
                    disabled={jobStatus === 'creating'}
                    variant="outline"
                    className="w-full"
                  >
                    <AppIcon path={mdiRefresh} size="md" className="mr-2" />
                    {t('testing.process_refresh')}
                  </Button>
                </div>
              </div>

              {/* Asset Type Hierarchy Jobs */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-foreground">{t('testing.asset_type_hierarchy')}</h3>
                <p className="text-xs text-muted-foreground mb-2">
                  {t('testing.asset_type_hierarchy_description')}
                </p>
                <Button
                  onClick={() => handleCreateAssetTypeHierarchyJob()}
                  disabled={jobStatus === 'creating'}
                  variant="outline"
                  className="w-full"
                  data-testid="create-asset-type-hierarchy-job"
                >
                  <AppIcon path={mdiSitemap} size="md" className="mr-2" />
                  {t('testing.refresh_type_hierarchy')}
                </Button>
              </div>

              {/* Email Jobs */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-foreground">{t('testing.email_service')}</h3>
                <p className="text-xs text-muted-foreground mb-2">
                  {t('testing.email_service_description')}
                </p>
                <Button
                  onClick={handleCreateEmailJob}
                  disabled={jobStatus === 'creating'}
                  variant="outline"
                  className="w-full"
                  data-testid="send-welcome-email"
                >
                  <AppIcon path={mdiCog} size="md" className="mr-2" />
                  {t('testing.send_welcome_email')}
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTestJobsOpen(false)}>
                {t('common:actions.close')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}